/**
 * Export Data Component
 *
 * Export trading bots and staking positions to CSV or JSON
 */

import { Icons } from '../Icons';
import type { TradingBot, StakingPosition } from '../../lib/api/cryptocom-api';

interface ExportDataProps {
  bots: TradingBot[];
  positions: StakingPosition[];
}

export function ExportData({ bots, positions }: ExportDataProps) {
  const exportToCSV = (data: any[], filename: string, headers: string[]) => {
    // Create CSV header
    const csvHeader = headers.join(',') + '\n';

    // Create CSV rows
    const csvRows = data.map(row => {
      return headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',');
    }).join('\n');

    const csv = csvHeader + csvRows;

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = (data: any, filename: string) => {
    const json = JSON.stringify(data, null, 2);

    // Download JSON
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportBotsCSV = () => {
    const headers = ['id', 'type', 'symbol', 'status', 'createdAt', 'profit', 'profitPercentage', 'trades', 'gridLevels'];
    const data = bots.map(bot => ({
      id: bot.id,
      type: bot.type,
      symbol: bot.symbol,
      status: bot.status,
      createdAt: new Date(bot.createdAt).toISOString(),
      profit: bot.profit.toFixed(2),
      profitPercentage: bot.profitPercentage.toFixed(2),
      trades: bot.trades,
      gridLevels: bot.gridLevels || ''
    }));

    exportToCSV(data, 'cryptocom_trading_bots', headers);
  };

  const handleExportBotsJSON = () => {
    exportToJSON(bots, 'cryptocom_trading_bots');
  };

  const handleExportPositionsCSV = () => {
    const headers = ['id', 'type', 'instrument', 'amount', 'apr', 'apy', 'startDate', 'totalRewards', 'pendingRewards', 'status', 'lockupPeriod'];
    const data = positions.map(pos => ({
      id: pos.id,
      type: pos.type,
      instrument: pos.instrument,
      amount: pos.amount.toFixed(2),
      apr: pos.apr.toFixed(2),
      apy: pos.apy.toFixed(2),
      startDate: new Date(pos.startDate).toISOString(),
      totalRewards: pos.totalRewards.toFixed(2),
      pendingRewards: pos.pendingRewards.toFixed(2),
      status: pos.status,
      lockupPeriod: pos.lockupPeriod || ''
    }));

    exportToCSV(data, 'cryptocom_staking_positions', headers);
  };

  const handleExportPositionsJSON = () => {
    exportToJSON(positions, 'cryptocom_staking_positions');
  };

  const handleExportAllJSON = () => {
    const allData = {
      exportDate: new Date().toISOString(),
      tradingBots: bots,
      stakingPositions: positions,
      summary: {
        totalBots: bots.length,
        activeBots: bots.filter(b => b.status === 'ACTIVE').length,
        totalTradingProfit: bots.reduce((sum, b) => sum + b.profit, 0),
        totalPositions: positions.length,
        totalStaked: positions.reduce((sum, p) => sum + p.amount, 0),
        totalRewards: positions.reduce((sum, p) => sum + p.totalRewards, 0)
      }
    };

    exportToJSON(allData, 'cryptocom_complete_export');
  };

  return (
    <div style={{
      padding: '16px',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      marginBottom: '24px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <Icons.Download size={18} />
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          Exporter les données
        </h3>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '12px'
      }}>
        {/* Export Bots */}
        {bots.length > 0 && (
          <>
            <button
              onClick={handleExportBotsCSV}
              style={{
                padding: '10px 16px',
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'var(--card)';
              }}
            >
              <Icons.FileText size={16} />
              <span>Bots CSV ({bots.length})</span>
            </button>

            <button
              onClick={handleExportBotsJSON}
              style={{
                padding: '10px 16px',
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'var(--card)';
              }}
            >
              <Icons.Code size={16} />
              <span>Bots JSON</span>
            </button>
          </>
        )}

        {/* Export Positions */}
        {positions.length > 0 && (
          <>
            <button
              onClick={handleExportPositionsCSV}
              style={{
                padding: '10px 16px',
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'var(--card)';
              }}
            >
              <Icons.FileText size={16} />
              <span>Staking CSV ({positions.length})</span>
            </button>

            <button
              onClick={handleExportPositionsJSON}
              style={{
                padding: '10px 16px',
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text)',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.background = 'var(--card)';
              }}
            >
              <Icons.Code size={16} />
              <span>Staking JSON</span>
            </button>
          </>
        )}

        {/* Export All */}
        {(bots.length > 0 || positions.length > 0) && (
          <button
            onClick={handleExportAllJSON}
            style={{
              padding: '10px 16px',
              background: 'var(--accent)',
              border: '1px solid var(--accent)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              gridColumn: 'span 2'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Icons.Download size={16} />
            <span>Export Complet JSON</span>
          </button>
        )}
      </div>

      {(bots.length === 0 && positions.length === 0) && (
        <p style={{
          fontSize: '13px',
          color: 'var(--muted)',
          textAlign: 'center',
          margin: 0
        }}>
          Aucune donnée disponible pour l'export
        </p>
      )}
    </div>
  );
}
